import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "./auth-context.js";

/**
 * Preferences provider — promoted in the App Shell + Design System
 * interstitial (2026-05-05) from the Phase 4 two-flag shape to a
 * four-field per-user JSON blob.
 *
 *   - `theme`         — "light" | "dark" — first-class dual theme. Andre
 *                       prefers dark; spouse prefers light. Single
 *                       `data-theme` attribute on <html> drives the
 *                       semantic-token swap defined in app.css.
 *   - `privacyMode`   — masks dollar values to `••••` (Phase 4.1).
 *   - `readOnlyMode`  — UX hint that hides write controls; backend
 *                       WRITE_ROLES gate stays authoritative (Phase 4.1).
 *   - `simpleView`    — spousal "headline numbers + decisions to revisit
 *                       + bills due" mode; promoted from Phase 11. Wired
 *                       in this interstitial; render branches land
 *                       throughout future sessions as features mature.
 *
 * **Per-user persistence.** Andre + spouse share devices, so a single
 * global localStorage key would have them clobbering each other on
 * sign-in. The new key shape is `hearth.preferences.${userId}.v1` as a
 * single JSON blob. On first read for a given user we lift the legacy
 * `hearth.privacy_mode` + `hearth.read_only_mode` keys into the new
 * shape so Phase-4-era state survives.
 *
 * **Pre-auth behavior.** The provider sits inside <AuthProvider> but
 * outside the <ProtectedRoute> tree, so it renders for /signin /signup
 * too. When `user` is null we keep state in memory only — no legacy-key
 * write — and seed `theme` from `prefers-color-scheme` so the first
 * paint of the sign-in page matches the OS preference. Once the user
 * resolves we hydrate from their per-user key, which wins from then on.
 */

const PREFS_KEY_PREFIX = "hearth.preferences.";
const PREFS_VERSION = "v1";
const LEGACY_PRIVACY_KEY = "hearth.privacy_mode";
const LEGACY_READ_ONLY_KEY = "hearth.read_only_mode";

export type Theme = "light" | "dark";

export interface PreferencesValue {
  readonly theme: Theme;
  readonly privacyMode: boolean;
  readonly readOnlyMode: boolean;
  readonly simpleView: boolean;
}

interface PreferencesContextValue extends PreferencesValue {
  readonly setTheme: (next: Theme) => void;
  readonly toggleTheme: () => void;
  readonly setPrivacyMode: (next: boolean) => void;
  readonly togglePrivacyMode: () => void;
  readonly setReadOnlyMode: (next: boolean) => void;
  readonly toggleReadOnlyMode: () => void;
  readonly setSimpleView: (next: boolean) => void;
  readonly toggleSimpleView: () => void;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

function userKey(userId: string): string {
  return `${PREFS_KEY_PREFIX}${userId}.${PREFS_VERSION}`;
}

function readJson(key: string): unknown {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* localStorage unavailable (private mode, disabled) — silently noop. */
  }
}

function readLegacyBool(key: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
}

function prefersDark(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
}

function defaultPreferences(): PreferencesValue {
  return {
    theme: prefersDark() ? "dark" : "light",
    privacyMode: readLegacyBool(LEGACY_PRIVACY_KEY),
    readOnlyMode: readLegacyBool(LEGACY_READ_ONLY_KEY),
    simpleView: false,
  };
}

function hydrateForUser(userId: string): PreferencesValue {
  const stored = readJson(userKey(userId));
  if (stored && typeof stored === "object") {
    const s = stored as Partial<PreferencesValue>;
    return {
      theme: s.theme === "light" || s.theme === "dark" ? s.theme : prefersDark() ? "dark" : "light",
      privacyMode: typeof s.privacyMode === "boolean" ? s.privacyMode : false,
      readOnlyMode: typeof s.readOnlyMode === "boolean" ? s.readOnlyMode : false,
      simpleView: typeof s.simpleView === "boolean" ? s.simpleView : false,
    };
  }
  // First-time hydration for this user — lift legacy global flags into the
  // namespaced key so the Phase 4.1 privacy + read-only choices survive.
  return defaultPreferences();
}

function applyThemeAttribute(theme: Theme): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
}

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.user_id ?? null;
  const [prefs, setPrefs] = useState<PreferencesValue>(() => defaultPreferences());

  // Track which userId we last hydrated for, so a userId change (sign-in,
  // user-swap on a shared MacBook) re-reads the appropriate per-user blob.
  const hydratedFor = useRef<string | null>(null);

  useEffect(() => {
    if (userId === hydratedFor.current) return;
    hydratedFor.current = userId;
    if (userId === null) {
      // Sign-out — fall back to defaults (keeps the OS theme preference).
      setPrefs(defaultPreferences());
      return;
    }
    setPrefs(hydrateForUser(userId));
  }, [userId]);

  // Persist on every change while authed. Pre-auth changes (e.g. flipping
  // theme on the sign-in page) stay in memory only — there's no userId
  // namespace to persist them under, and writing to the legacy global keys
  // would defeat the per-user-isolation rationale for the migration.
  useEffect(() => {
    if (!userId) return;
    writeJson(userKey(userId), prefs);
  }, [userId, prefs]);

  // Apply the data-theme attribute on every theme change, including the
  // initial mount. Single source of truth for the app.css token swap.
  useEffect(() => {
    applyThemeAttribute(prefs.theme);
  }, [prefs.theme]);

  const setTheme = useCallback(
    (next: Theme) => setPrefs((p) => ({ ...p, theme: next })),
    [],
  );
  const toggleTheme = useCallback(
    () => setPrefs((p) => ({ ...p, theme: p.theme === "dark" ? "light" : "dark" })),
    [],
  );
  const setPrivacyMode = useCallback(
    (next: boolean) => setPrefs((p) => ({ ...p, privacyMode: next })),
    [],
  );
  const togglePrivacyMode = useCallback(
    () => setPrefs((p) => ({ ...p, privacyMode: !p.privacyMode })),
    [],
  );
  const setReadOnlyMode = useCallback(
    (next: boolean) => setPrefs((p) => ({ ...p, readOnlyMode: next })),
    [],
  );
  const toggleReadOnlyMode = useCallback(
    () => setPrefs((p) => ({ ...p, readOnlyMode: !p.readOnlyMode })),
    [],
  );
  const setSimpleView = useCallback(
    (next: boolean) => setPrefs((p) => ({ ...p, simpleView: next })),
    [],
  );
  const toggleSimpleView = useCallback(
    () => setPrefs((p) => ({ ...p, simpleView: !p.simpleView })),
    [],
  );

  const value = useMemo<PreferencesContextValue>(
    () => ({
      ...prefs,
      setTheme,
      toggleTheme,
      setPrivacyMode,
      togglePrivacyMode,
      setReadOnlyMode,
      toggleReadOnlyMode,
      setSimpleView,
      toggleSimpleView,
    }),
    [
      prefs,
      setTheme,
      toggleTheme,
      setPrivacyMode,
      togglePrivacyMode,
      setReadOnlyMode,
      toggleReadOnlyMode,
      setSimpleView,
      toggleSimpleView,
    ],
  );

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences(): PreferencesContextValue {
  const ctx = useContext(PreferencesContext);
  if (!ctx) {
    throw new Error("usePreferences must be used inside <PreferencesProvider>");
  }
  return ctx;
}
